import { Header } from '../common/Header';
import Card from '@mui/material/Card';
import TableGrid from '../table-grid/TableGrid';
import { EndPoints } from '../../axios/endPoints';
import { GridColDef } from '@mui/x-data-grid';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import moment from 'moment';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { handleCreatorRowClick, handleRefreshMLCreatorTable, handleToggleDeleteFilePopup, handleToggleFileDetailsPopup, handleToggleImportTrainingDataDialog } from '../../store/mlcreator-slice/mlcreator';
import ImportCreatorTrainingData from './ImportCreatorTrainingData';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { DownloadFile } from '../../services/FileServices';
import { useTranslation } from 'react-i18next';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import React, { useEffect, useMemo, useRef } from 'react';
import DeleteFilePopup from './DeleteFilePopup';
import CreatorFileDetails from './CreatorFileDetails';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import RefreshIcon from '@mui/icons-material/Refresh';
import Old_SelectedMLCreator from './old_SelectedMLCreator';

const Old_MLCreator = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const selectedCreatorRef = useRef<HTMLDivElement>();
    const memoizedCreatorRef = React.useMemo(() => selectedCreatorRef, [selectedCreatorRef]);
    const { refreshMLCreatorTable, selectedCreatorRow } = useAppSelector((state: RootState) => state.mlcreator);

    const columns: GridColDef[] = useMemo(() => [
        { field: 'id', headerName: t('CREATOR_ID_FIELD') },
        {
            field: 'name',
            headerName: t('CREATOR_NAME_FIELD'),
            minWidth: 350,
            renderCell: (params) => {
                return (<div className="styled-div">{params.value}</div>)
            }
        },
        {
            field: 'modelCount',
            headerName: t('CREATOR_MODEL_COUNT_FIELD'),
            renderCell: (params) => {
                return (<div className="styled-div">{params.value}</div>)
            }
        },
        { field: 'fileName', headerName: t('CREATOR_FILE_NAME_FIELD'), minWidth: 350 },
        { field: 'rowCount', headerName: t('CREATOR_ROW_COUNT_FIELD') },
        { field: 'columnCount', headerName: t('CREATOR_COLUMN_COUNT_FIELD') },
        {
            field: 'uploadDate', headerName: t('CREATOR_UPLOAD_DATE_FIELD'), renderCell: (params) => {
                return moment.utc(params.value).local().format('DD/MM/YYYY HH:mm')
            }, minWidth: 200
        },
        {
            field: 'actions', headerName: t('CREATOR_ACTIONS_FIELD'), minWidth: 300, renderCell: (params) => {
                return (
                    <Grid container spacing={3}>
                        <Grid item>
                            <Tooltip title={t('CREATOR_DETAILS_ACTION')}>
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(handleToggleFileDetailsPopup({
                                        open: true,
                                        body: {
                                            fileId: params.row.id,
                                            fileName: params.row.fileName
                                        }
                                    }))
                                }}>
                                    <InfoTwoToneIcon className='info-icon' />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title={t('CREATOR_DOWNLOAD_ACTION')}>
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    DownloadFile(EndPoints.ML_CREATOR_DOWNLOAD_TRAINING_DATASET + `?id=${params.row.id}`)
                                }}>
                                    <CloudDownloadTwoToneIcon className="download-icon" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title={t('CREATOR_TRAIN_NEW_MODEL_ACTION')}>
                                <IconButton onClick={async (e) => {
                                    await dispatch(handleCreatorRowClick(params.row));
                                    if (selectedCreatorRef && selectedCreatorRef.current) {
                                        selectedCreatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                }}>
                                    <PlayCircleIcon className="play-icon" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title={t('CREATOR_DELETE_ACTION')}>
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(handleToggleDeleteFilePopup({
                                        open: true, body: {
                                            fileName: params.row.fileName,
                                            fileId: params.row.id
                                        }
                                    }))
                                }}>
                                    <DeleteForeverTwoToneIcon className="delete-icon" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                )
            }
        }
    ], [dispatch, t])

    const handleToggleImportTrainingData = (isOpen: boolean) => {
        dispatch(handleToggleImportTrainingDataDialog(isOpen));
    }

    useEffect(() => {
        return () => dispatch(handleCreatorRowClick(null));
    }, [])

    return (
        <React.Fragment>
            <Card style={{ marginTop: '5px' }}>
                <Header
                    title={t('SIDEBAR_ML_CREATOR_TEXT')}
                    helperIcon={
                        <RedirectIconButton to={'/help/2'} className="helper-icon" >
                            <ContactSupportIcon />
                        </RedirectIconButton>
                    }
                />
                <Grid container p={2} spacing={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Grid item display="flex" justifyContent="space-between">
                        <Button
                            onClick={() => { handleToggleImportTrainingData(true) }}
                            variant='contained'
                            className='button-color'
                            size="small"
                        >
                            {t('CREATOR_IMPORT_TRAINING_DATA_BUTTON')}
                        </Button>
                        <Tooltip title={t('REFRESH_TABLE_BUTTON_TOOLTIP')}>
                            <IconButton onClick={() => dispatch(handleRefreshMLCreatorTable())}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item sx={{ width: '100%', overflowY: 'auto' }}>
                        <TableGrid
                            baseURL={EndPoints.ML_CREATOR_GET_TRAINING_DATASETS}
                            columns={columns}
                            sortColumns={[{ field: 'id', sort: 'asc' }]}
                            refresh={refreshMLCreatorTable}
                            disableRowSelection
                            tableHeight={500}
                            onCellClick={async (params: any) => {
                                if (params.field == 'name' || params.field == 'modelCount') {
                                    await dispatch(handleCreatorRowClick(params.row));
                                    if (selectedCreatorRef && selectedCreatorRef.current) {
                                        selectedCreatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                }
                            }}
                            getCellClassName={(params: any) => {
                                if (params.field == 'name' || params.field == 'modelCount') return 'custom-pointer-cell';
                            }}
                            rowHeight={35}
                            columnHeaderHeight={40}
                            getRowClassName={(params: any) =>
                                params.id === selectedCreatorRow?.id ? 'highlighted-row' : ''
                            }
                        />
                    </Grid>
                </Grid>
                <ImportCreatorTrainingData />
                <DeleteFilePopup />
                <CreatorFileDetails />
            </Card>
            <br />
            <Old_SelectedMLCreator ref={memoizedCreatorRef as React.RefObject<HTMLDivElement>} />
        </React.Fragment>
    )
}

export default Old_MLCreator;